import { useId, useState, type ReactNode } from 'react';
import { ChevronRightIcon } from './icons';
import styles from './SidebarSection.module.css';

interface SidebarSectionProps {
  id: string;
  title: string;
  activeLabel?: string | null;
  actions?: ReactNode;
  children: ReactNode;
  forceOpen?: boolean;
  onCollapse?: () => void;
}

/** Local display preference, shared by every newtab on the same origin. */
export function SidebarSection({ id, title, activeLabel, actions, children, forceOpen = false, onCollapse }: SidebarSectionProps) {
  const contentId = useId();
  const storageKey = `mc_sidebar_${id}_collapsed`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; }
    catch { return false; }
  });
  const expanded = forceOpen || !collapsed;
  const toggle = () => {
    const next = expanded;
    setCollapsed(next);
    if (next) onCollapse?.();
    try { localStorage.setItem(storageKey, String(next)); }
    catch { /* Navigation still works when browser storage is unavailable. */ }
  };
  return (
    <section className={styles.section}>
      <div className={styles.heading}>
        <h2 className={styles.title}>
          <button type="button" className={styles.toggle} onClick={toggle}
            aria-expanded={expanded} aria-controls={contentId}>
            <ChevronRightIcon size={12} className={expanded ? styles.open : undefined} />
            <span>{title}</span>
            {!expanded && activeLabel && <span className={styles.active} title={activeLabel}>{activeLabel}</span>}
          </button>
        </h2>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      <div id={contentId} hidden={!expanded}>{children}</div>
    </section>
  );
}
